/**
 * Insights — Roster & MIPS Exposure (P8; rebuilt 2026-07-15 per owner feedback):
 *  - GROUP-LEVEL rollup is primary — practice/TIN rows lead, clinicians are
 *    drill-down only (reporting happens at the entity level, not per NPI).
 *  - The APPLIED score drives the report: the score strip carries the current
 *    APP quality composite (from QPS Management) into the §7.4 MIPS exposure
 *    math. PI is a manual projection until PI Reporting (P9) auto-fills it.
 *  - Gap-Management-style chrome: filter bar (search + must-report toggle)
 *    and CSV export over the filtered rows.
 *  - The score strip is EXPANDABLE (QPS pattern): branches, break-evens, and
 *    the report-vs-reweight advice live in its details — no separate bottom
 *    panel. TIN-match issues surface at the UPLOAD (CMS Reports), not here
 *    (owner 2026-07-15: masked TINs are the export's normal shape).
 * All calculations resolve through the YEAR'S logic module (D13).
 */
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, ChevronRight, Download, Search,
  ShieldCheck, Users, FileWarning, Percent,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useAco } from '@/contexts/AcoContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { logicForYear } from '@/lib/app-plus/years/registry';
import {
  useAcoParticipants, useAcoRoster, useAcoRosterDerived,
} from '@/hooks/queries/useMipsQueries';
import { useAppPlusScoring } from './lib/useAppPlusScoring';
import { AcoHeader, NoAcoState, YearUnsupportedState } from './components/AcoHeader';

interface RosterRow {
  tin: string;
  npi: string;
  clinician_name: string | null;
  must_report_class: 'required' | 'not_required_lvt' | 'not_available' | null;
  mips_eligible_type: boolean | null;
  meets_lvt: boolean | null;
  qp_status: string | null;
  apm_medicare_qp_score: string | null;
  small_group_practitioner: boolean | null;
  hospital_based_clinician: boolean | null;
  non_patient_facing: boolean | null;
}

interface Derived {
  imported: boolean;
  clinicians: number;
  practices: number;
  mustReport: { required: number; notRequiredLvt: number; notAvailable: number };
  qps: number;
  /** Reweight determination over MIPS ELIGIBLE CLINICIANS only (spec §7.2). */
  reweight: {
    qualified: number; mipsEcs: number; ratioPct: number | null;
    allQualified: boolean; entityThresholdPct: number;
  };
  coverage: { status: string; mustReportCovered: number };
}

interface PracticeGroup {
  /** Practice name is the analytics key (owner 2026-07-15) — TIN may be masked. */
  name: string;
  tin: string;
  rows: RosterRow[];
}

const isQp = (r: RosterRow) => r.qp_status === 'Y' || r.qp_status === 'P';
/** Exposed = must-report AND not a QP: the clinicians the §7.4 floor actually reaches. */
const isExposed = (r: RosterRow) => r.must_report_class === 'required' && !isQp(r);

function toCsv(groups: PracticeGroup[]): string {
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const yn = (v: boolean | null) => (v == null ? '' : v ? 'Yes' : 'No');
  const header = [
    'Practice', 'TIN', 'NPI', 'Clinician', 'Must Report', 'MIPS-Eligible Type',
    'Meets LVT', 'QP Status', 'Exposed (must-report non-QP)', 'Small Practice',
    'Hospital-Based', 'Non-Patient-Facing', 'Medicare QP Score',
  ];
  const lines = [header.join(',')];
  for (const g of groups) {
    for (const r of g.rows) {
      lines.push([
        esc(g.name), esc(g.tin), esc(r.npi), esc(r.clinician_name ?? ''),
        esc(r.must_report_class ?? ''), yn(r.mips_eligible_type), yn(r.meets_lvt),
        esc(r.qp_status ?? ''), isExposed(r) ? 'Yes' : 'No',
        yn(r.small_group_practitioner), yn(r.hospital_based_clinician), yn(r.non_patient_facing),
        esc(r.apm_medicare_qp_score ?? ''),
      ].join(','));
    }
  }
  return lines.join('\n');
}

export default function InsightsRoster() {
  const navigate = useNavigate();
  const { selectedAco, loading: acoLoading } = useAco();
  const { programYear } = useProgramYear();
  const scoring = useAppPlusScoring(selectedAco?.id ?? null, selectedAco?.shadow_practice_id ?? null);

  const rosterQ = useAcoRoster(selectedAco?.id ?? null, programYear);
  const participantsQ = useAcoParticipants(selectedAco?.id ?? null, programYear);
  const derivedQ = useAcoRosterDerived(selectedAco?.id ?? null, programYear);
  const roster: RosterRow[] | null = rosterQ.data
    ? (rosterQ.data.roster ?? [])
    : (rosterQ.isPending ? null : []);
  const participants: Array<{ tin: string; organization_name: string }> =
    participantsQ.data?.participants ?? [];
  const derived = (derivedQ.data ?? null) as Derived | null;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [piInput, setPiInput] = useState('');
  const [stripOpen, setStripOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [mustReportOnly, setMustReportOnly] = useState(false);

  const logic = useMemo(() => {
    try { return logicForYear(programYear); } catch { return null; }
  }, [programYear]);

  // Group by PRACTICE NAME (the participants table carries the org name per TIN).
  const groups: PracticeGroup[] = useMemo(() => {
    if (!roster) return [];
    const nameByTin = new Map(participants.map(p => [p.tin, p.organization_name]));
    const byName = new Map<string, PracticeGroup>();
    for (const row of roster) {
      const name = nameByTin.get(row.tin) || row.tin;
      if (!byName.has(name)) byName.set(name, { name, tin: row.tin, rows: [] });
      byName.get(name)!.rows.push(row);
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [roster, participants]);

  // Filter bar: search matches practice / TIN / clinician / NPI; must-report
  // toggle narrows clinician rows (practices with no survivors drop out).
  const filteredGroups: PracticeGroup[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups
      .map(g => {
        const practiceHit = !q || g.name.toLowerCase().includes(q) || g.tin.toLowerCase().includes(q);
        let rows = g.rows;
        if (q && !practiceHit) {
          rows = rows.filter(r =>
            (r.clinician_name ?? '').toLowerCase().includes(q) || r.npi.toLowerCase().includes(q));
        }
        if (mustReportOnly) rows = rows.filter(r => r.must_report_class === 'required');
        return { ...g, rows };
      })
      .filter(g => g.rows.length > 0);
  }, [groups, search, mustReportOnly]);

  if (!scoring.yearSupported) return <YearUnsupportedState year={programYear} />;
  if (acoLoading || scoring.loading || roster === null) {
    // Shell-first (owner 2026-07-20): page chrome renders immediately.
    return (
      <div>
        <AcoHeader title="Roster & MIPS Exposure" subtitle="Participant roster analytics and §7.4 MIPS exposure" showReportBar />
        <Skeleton className="h-24 w-full rounded-lg" />
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map(n => <Skeleton key={n} className="h-32 rounded-lg" />)}
        </div>
        <Skeleton className="mt-4 h-96 w-full rounded-lg" />
      </div>
    );
  }
  if (!selectedAco) return <NoAcoState />;

  // The APPLIED score (owner 2026-07-15): the current QPS composite feeds the
  // exposure math. Q = raw §3 composite (QPS floors never reach MIPS).
  const quality = scoring.applied?.mipsQualityInput ?? null;
  const constants = logic ? logic.getConstants(programYear) : null;
  const pi = piInput === '' ? null : Number(piInput);
  const exposure = logic && constants && quality != null
    ? logic.computeMipsExposure({ qualityPct: quality, piPct: pi }, constants)
    : null;
  // Entity fully reweights (all MIPS ECs qualified) → simplified strip (owner-
  // approved 2026-07-15): the determination IS the reweighted branch, the PI
  // input demotes to a voluntary-reporting what-if inside Details. Data-driven:
  // rosters with non-excluded ECs keep the full surface.
  const fullReweight = derived?.reweight.allQualified ?? false;
  // Lead branch: the reweighted determination when the entity fully reweights;
  // otherwise reported-when-a-PI-projection-is-entered, else PI-reweighted.
  const lead = exposure
    ? (!fullReweight && pi != null && exposure.reported ? exposure.reported : exposure.reweighted)
    : null;
  const leadLabel = fullReweight
    ? 'PI reweighted — automatic'
    : pi != null && exposure?.reported ? 'Reported (with projected PI)' : 'PI reweighted';
  const exposedCount = roster.filter(isExposed).length;

  const exportCsv = () => {
    const csv = toCsv(filteredGroups);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedAco.aco_id_cms || 'aco'}-roster-exposure-${programYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statCard = (icon: React.ReactNode, label: string, value: React.ReactNode, sub: string) => (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-1.5">{icon}{label}</CardDescription>
        <CardTitle className="text-3xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">{sub}</CardContent>
    </Card>
  );

  return (
    <div>
      <div className="mb-2">
        <Button variant="ghost" size="sm" className="-ml-2 text-muted-foreground"
          onClick={() => navigate('/programs/app-plus/insights')}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> All insights
        </Button>
      </div>
      <AcoHeader title="Roster & MIPS Exposure" subtitle="Participant roster analytics and §7.4 MIPS exposure" showReportBar />

      {!derived?.imported ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <Users className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="font-medium">No participant roster for PY {programYear}</p>
            <p className="mt-1 text-sm">
              Upload the ACO Eligibility File in <span className="font-medium text-foreground">Configuration → CMS Reports</span> to unlock this page.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ===== Score strip (lead): applied composite → §7.4 exposure.
                 Expandable (QPS pattern) — branches, break-evens, and the
                 report-vs-reweight advice live in the details ===== */}
          <div
            className={cn('rounded-lg border bg-card p-4',
              quality != null && 'cursor-pointer transition-colors hover:bg-muted/50')}
            onClick={() => quality != null && setStripOpen(o => !o)}
            role="button"
            aria-expanded={stripOpen}
          >
            {quality == null || !lead || !exposure ? (
              <p className="text-sm text-muted-foreground">
                No applied score yet — enter measure data on{' '}
                <span className="font-medium text-foreground">QPS Management</span> to compute the
                MIPS exposure this roster carries.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Applied Quality Composite</p>
                      <p className="text-[30px] leading-[45px] font-semibold mt-0.5 tabular-nums">{quality.toFixed(2)}</p>
                    </div>
                    {!fullReweight && (
                      <>
                        <div className="h-10 w-px bg-border" />
                        <div onClick={e => e.stopPropagation()}>
                          <p className="text-xs text-muted-foreground uppercase tracking-wide">Projected Entity PI</p>
                          <div className="mt-1.5 flex items-center gap-2">
                            <Input className="h-9 w-24 text-center tabular-nums" type="number" min={0} max={100}
                              placeholder="—" value={piInput} onChange={e => setPiInput(e.target.value)} />
                            <span className="text-xs text-muted-foreground max-w-[160px] leading-tight">
                              auto-fills when PI Reporting lands
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="h-10 w-px bg-border" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">MIPS Final ({leadLabel})</p>
                      <p className="text-[30px] leading-[45px] font-semibold mt-0.5 tabular-nums">
                        {lead.finalScore.toFixed(1)}
                      </p>
                    </div>
                    <div className="h-10 w-px bg-border" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Payment Adjustment</p>
                      <p className={cn('text-[24px] leading-[45px] font-semibold mt-0.5 tabular-nums',
                        lead.positiveScaled ? 'text-emerald-600'
                          : (lead.adjustmentPct ?? 0) < 0 ? 'text-red-600' : 'text-muted-foreground')}>
                        {lead.positiveScaled ? 'Positive (scaled)' : `${lead.adjustmentPct}%`}
                      </p>
                    </div>
                    <div className="h-10 w-px bg-border" />
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Exposed Clinicians</p>
                      <p className="text-[24px] leading-[45px] font-semibold mt-0.5 tabular-nums">
                        {exposedCount}<span className="text-sm text-muted-foreground font-normal"> of {derived.clinicians}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                    <span>Details</span>
                    {stripOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </div>
                </div>
                <p className="mt-2 border-t pt-2 text-xs text-muted-foreground">
                  {fullReweight ? (
                    <>
                      <span className="font-medium text-foreground">
                        Entity PI reweights automatically — all {derived.reweight.mipsEcs} MIPS ECs qualify.
                      </span>{' '}
                      No PI reporting obligation (PY2026 enforcement discretion also extended).
                      Applied score from QPS Management (raw §3 composite); applies as a FLOOR to
                      must-report, non-QP clinicians ({derived.qps} QPs receive no adjustment).
                      Payment lands {exposure.paymentYearLabel}.
                    </>
                  ) : (
                    <>
                      Applied score from QPS Management (raw §3 composite — QPS floors never reach MIPS).
                      Applies as a FLOOR to must-report, non-QP clinicians; {derived.qps} QPs receive no
                      adjustment. Payment lands {exposure.paymentYearLabel}. PI is a manual projection
                      until PI Reporting arrives.
                    </>
                  )}
                </p>
                {stripOpen && (
                  <div className="mt-3 space-y-3 border-t pt-3" onClick={e => e.stopPropagation()}>
                    <div className="grid gap-3 lg:grid-cols-3">
                      <div className="rounded-lg border p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold">
                            {fullReweight ? 'Voluntary reporting (what-if)' : 'Reported PI'}
                          </p>
                          {fullReweight && (
                            <Input className="h-7 w-20 text-center tabular-nums" type="number" min={0} max={100}
                              placeholder="PI?" value={piInput} onChange={e => setPiInput(e.target.value)} />
                          )}
                        </div>
                        <p className="mt-1 text-2xl font-bold tabular-nums">
                          {exposure.reported ? exposure.reported.finalScore.toFixed(1) : '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {exposure.reported
                            ? exposure.reported.positiveScaled
                              ? 'Above 75 — positive, budget-neutrality-scaled'
                              : `Adjustment ${exposure.reported.adjustmentPct}%`
                            : fullReweight ? 'Enter a what-if entity PI score' : 'Enter a projected entity PI above'}
                          {' '}· 0.5×Q + 0.3×PI + 20 (IA auto-credit)
                        </p>
                      </div>
                      <div className="rounded-lg border p-3">
                        <p className="text-sm font-semibold">PI reweighted</p>
                        <p className="mt-1 text-2xl font-bold tabular-nums">{exposure.reweighted.finalScore.toFixed(1)}</p>
                        <p className="text-xs text-muted-foreground">
                          {exposure.reweighted.positiveScaled
                            ? 'Above 75 — positive, scaled'
                            : `Adjustment ${exposure.reweighted.adjustmentPct}%`}
                          {' '}· 0.75×Q + 25 · quality break-even {exposure.breakEvens.reweightedQualityMin}
                        </p>
                      </div>
                      <div className={cn('rounded-lg border p-3',
                        derived.reweight.allQualified ? 'border-muted bg-muted/30' : 'border-red-200 bg-red-50/50')}>
                        <p className={cn('text-sm font-semibold', derived.reweight.allQualified ? 'text-muted-foreground' : 'text-red-800')}>
                          Must-report PI zero {derived.reweight.allQualified ? '(does not apply)' : '(red flag)'}
                        </p>
                        <p className={cn('mt-1 text-2xl font-bold tabular-nums', derived.reweight.allQualified ? 'text-muted-foreground' : 'text-red-700')}>
                          {exposure.piZero.finalScore.toFixed(1)}
                        </p>
                        <p className={cn('text-xs', derived.reweight.allQualified ? 'text-muted-foreground' : 'text-red-700/80')}>
                          {derived.reweight.allQualified
                            ? 'Every MIPS EC qualifies for reweighting — nobody on this roster faces this case'
                            : <>Adjustment {exposure.piZero.adjustmentPct}% · max attainable 70 — quality cannot rescue it</>}
                        </p>
                      </div>
                    </div>
                    <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                      <div>
                        <span className="font-medium text-foreground">Report vs. stay reweighted:</span>{' '}
                        for reweight-qualified units, reporting only wins if projected PI exceeds{' '}
                        <span className="font-medium text-foreground tabular-nums">{exposure.breakEvens.piCrossover.toFixed(1)}</span>{' '}
                        at this quality composite — and submitting overrides the reweighting for everyone
                        in the submitting unit (one-way).
                      </div>
                      <div>
                        <span className="font-medium text-foreground">PI-reweight determination:</span>{' '}
                        {derived.reweight.qualified} of {derived.reweight.mipsEcs} MIPS eligible clinicians
                        qualify ({derived.reweight.ratioPct ?? 0}%) —{' '}
                        {derived.reweight.allQualified
                          ? <span className="text-emerald-700 font-medium">ALL MIPS ECs qualify: the entity's PI reweights automatically — no PI reporting obligation; the reweighted branch applies</span>
                          : (derived.reweight.ratioPct ?? 0) >= derived.reweight.entityThresholdPct
                            ? <span className="text-emerald-700 font-medium">at/above the {derived.reweight.entityThresholdPct}% threshold — the entity EUC application path is open</span>
                            : <>below the {derived.reweight.entityThresholdPct}% entity EUC threshold</>}.
                        CPB treated as 0 (optional estimate).
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Derived-output cards */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {statCard(<Users className="h-3.5 w-3.5" />, 'Must-report coverage',
              <>{derived.mustReport.required}<span className="text-base text-muted-foreground font-normal"> of {derived.clinicians}</span></>,
              'Must-report clinicians — all covered by the ACO\u2019s APP submission (per-unit coverage arrives with PI Reporting)')}
            {statCard(<ShieldCheck className="h-3.5 w-3.5" />, '§425.507 check',
              derived.coverage.status === 'covered_by_entity_submission'
                ? <span className="text-emerald-600">PASS</span>
                : <span className="text-muted-foreground">n/a</span>,
              'Entity-level APP reporting covers every must-report clinician')}
            {statCard(<FileWarning className="h-3.5 w-3.5" />, 'Public reporting',
              <>{derived.clinicians}<span className="text-base text-muted-foreground font-normal"> / {derived.practices}</span></>,
              'Clinicians / practices on the certified participant list')}
            {statCard(<Percent className="h-3.5 w-3.5" />, 'PI-reweight qualified',
              derived.reweight.allQualified
                ? <span className="text-emerald-600">100%</span>
                : <>{derived.reweight.ratioPct ?? 0}%</>,
              derived.reweight.allQualified
                ? `All ${derived.reweight.mipsEcs} MIPS eligible clinicians qualify — entity PI reweights automatically`
                : `${derived.reweight.qualified} of ${derived.reweight.mipsEcs} MIPS eligible clinicians · entity threshold ${derived.reweight.entityThresholdPct}%`)}
          </div>

          {/* Practice-grouped roster (name-keyed — masked TINs display as-is) */}
          <Card className="mt-4">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-2xl font-bold">Participant Practices</CardTitle>
                  <CardDescription>
                    Grouped by practice name · {filteredGroups.length} of {groups.length} practices ·{' '}
                    {filteredGroups.reduce((n, g) => n + g.rows.length, 0)} clinicians shown
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-3 quality-print-hide">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input className="h-9 w-56 pl-8" placeholder="Practice, TIN, clinician, NPI…"
                      value={search} onChange={e => setSearch(e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="must-report-only" checked={mustReportOnly} onCheckedChange={setMustReportOnly} />
                    <Label htmlFor="must-report-only" className="text-sm text-muted-foreground cursor-pointer">
                      Must-report only
                    </Label>
                  </div>
                  <Button variant="outline" size="sm" onClick={exportCsv}>
                    <Download className="mr-1.5 h-4 w-4" /> Export CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Practice</TableHead>
                    <TableHead className="text-center">TIN</TableHead>
                    <TableHead className="text-center">Clinicians</TableHead>
                    <TableHead className="text-center">Must report</TableHead>
                    <TableHead className="text-center">Exposed</TableHead>
                    <TableHead className="text-center">MIPS-type / LVT</TableHead>
                    <TableHead className="text-center">QPs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                        No practices match the current filters.
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredGroups.map((g, i) => {
                    const isOpen = !!expanded[g.name];
                    const must = g.rows.filter(r => r.must_report_class === 'required').length;
                    const exposed = g.rows.filter(isExposed).length;
                    const qps = g.rows.filter(isQp).length;
                    return (
                      <Fragment key={g.name}>
                        <TableRow
                          className={cn('cursor-pointer hover:bg-muted/50 transition-colors',
                            i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}
                          onClick={() => setExpanded(prev => ({ ...prev, [g.name]: !isOpen }))}>
                          <TableCell className="text-left font-medium">
                            <span className="inline-flex items-center gap-1.5">
                              {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                              {g.name}
                            </span>
                          </TableCell>
                          <TableCell className="text-center tabular-nums text-muted-foreground">{g.tin}</TableCell>
                          <TableCell className="text-center tabular-nums">{g.rows.length}</TableCell>
                          <TableCell className="text-center tabular-nums">{must} of {g.rows.length}</TableCell>
                          <TableCell className="text-center">
                            {exposed > 0
                              ? <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 tabular-nums">{exposed}</Badge>
                              : '—'}
                          </TableCell>
                          <TableCell className="text-center tabular-nums">
                            {g.rows.filter(r => r.mips_eligible_type).length} / {g.rows.filter(r => r.meets_lvt).length}
                          </TableCell>
                          <TableCell className="text-center tabular-nums">{qps || '—'}</TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell colSpan={7} className="p-0">
                              <div className="border-t border-l-4 border-l-blue-500 p-3">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="text-left">Clinician</TableHead>
                                      <TableHead className="text-center">NPI</TableHead>
                                      <TableHead className="text-center">Must report</TableHead>
                                      <TableHead className="text-center">Special statuses</TableHead>
                                      <TableHead className="text-center">Medicare QP score</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {g.rows.map(r => (
                                      <TableRow key={r.npi}>
                                        <TableCell className="text-left">{r.clinician_name ?? '—'}</TableCell>
                                        <TableCell className="text-center tabular-nums">{r.npi}</TableCell>
                                        <TableCell className="text-center">
                                          {r.must_report_class === 'required'
                                            ? <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50">Required</Badge>
                                            : r.must_report_class === 'not_required_lvt'
                                              ? <Badge variant="outline" className="text-muted-foreground">Not required (LVT)</Badge>
                                              : <Badge variant="outline" className="text-muted-foreground">Not available</Badge>}
                                        </TableCell>
                                        <TableCell className="text-center text-xs text-muted-foreground">
                                          {[r.small_group_practitioner && 'Small practice',
                                            r.hospital_based_clinician && 'Hospital-based',
                                            r.non_patient_facing && 'Non-patient-facing']
                                            .filter(Boolean).join(' · ') || '—'}
                                        </TableCell>
                                        <TableCell className="text-center tabular-nums">
                                          {r.apm_medicare_qp_score ?? '—'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

        </>
      )}
    </div>
  );
}
